import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  FlaskConical,
  Link2,
  RadioTower,
  Scale,
  TrendingDown,
  TrendingUp,
  Unlink,
} from "lucide-react";
import EmptyState from "@/components/EmptyState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { API_URL } from "@/lib/api";
import { TRADING_MODES, getModeLabel } from "@/lib/tradingMode";

const API = API_URL;
const SAMPLE_MIN = 30;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

function formatNumber(value, digits = 2, { suffix = "", sign = false } = {}) {
  if (value === null || value === undefined) {
    return "—";
  }
  const numeric = Number(value);
  const prefix = sign && numeric > 0 ? "+" : "";
  return `${prefix}${numeric.toFixed(digits)}${suffix}`;
}

function modeMeta(mode) {
  return mode === TRADING_MODES.BACKTESTING
    ? {
        icon: FlaskConical,
        label: getModeLabel(TRADING_MODES.BACKTESTING),
        badgeClass: "border-primary/40 bg-primary/15 text-primary",
        barClass: "bg-primary",
      }
    : {
        icon: RadioTower,
        label: getModeLabel(TRADING_MODES.LIVE),
        badgeClass: "border-destructive/40 bg-destructive/15 text-destructive",
        barClass: "bg-destructive",
      };
}

function MetricRow({ label, value, emphasis = false }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className={emphasis ? "text-base font-semibold" : "font-medium"}>{value}</span>
    </div>
  );
}

function SideCard({ side }) {
  const { strategy, metrics } = side;
  const meta = modeMeta(strategy.mode);
  const Icon = meta.icon;
  const currency = strategy.currency;

  return (
    <Card className="shadow-sm" data-testid={`comparison-side-${strategy.mode}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={`flex h-10 w-10 items-center justify-center rounded-lg border ${meta.badgeClass}`}>
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-base">{strategy.name}</CardTitle>
              <CardDescription>{meta.label}</CardDescription>
            </div>
          </div>
          <Badge variant="outline" className={meta.badgeClass}>
            {metrics.total_trades} trades
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-2.5 text-sm">
        <MetricRow
          label="Expectancy (R)"
          value={formatNumber(metrics.expectancy_r, 2, { sign: true })}
          emphasis
        />
        <MetricRow
          label={`Expectancy (${currency})`}
          value={formatNumber(metrics.expectancy, 2, { sign: true })}
        />
        <MetricRow label="Win rate" value={formatNumber(metrics.win_rate, 1, { suffix: "%" })} />
        <MetricRow label="Profit factor" value={formatNumber(metrics.profit_factor, 2)} />
        <MetricRow
          label={`Total P/L (${currency})`}
          value={formatNumber(metrics.total_pnl, 2, { sign: true })}
        />
        <MetricRow label="Max drawdown" value={formatNumber(metrics.max_drawdown, 2, { suffix: "%" })} />
        <MetricRow
          label="Return on capital"
          value={formatNumber(metrics.return_on_capital, 2, { suffix: "%", sign: true })}
        />
        <p className="pt-2 text-xs text-muted-foreground">
          R sample: {metrics.r_sample} of {metrics.total_trades} trades
          {metrics.first_trade_date
            ? ` · ${metrics.first_trade_date.slice(0, 10)} → ${metrics.last_trade_date.slice(0, 10)}`
            : ""}
        </p>
      </CardContent>
    </Card>
  );
}

function CompareBar({ label, backtestValue, liveValue, format }) {
  const max = Math.max(Math.abs(backtestValue ?? 0), Math.abs(liveValue ?? 0), 1e-9);
  const rows = [
    { key: "backtest", label: "Backtest", value: backtestValue, barClass: "bg-primary" },
    { key: "live", label: "Live", value: liveValue, barClass: "bg-destructive" },
  ];

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium text-foreground">{label}</span>
      </div>
      {rows.map((row) => (
        <div key={row.key} className="flex items-center gap-2">
          <span className="w-16 text-xs text-muted-foreground">{row.label}</span>
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-secondary/60">
            <div
              className={`h-full rounded-full ${row.barClass}`}
              style={{ width: `${Math.min((Math.abs(row.value ?? 0) / max) * 100, 100)}%` }}
            />
          </div>
          <span className="w-16 text-right text-xs font-medium">{format(row.value)}</span>
        </div>
      ))}
    </div>
  );
}

function GapPanel({ backtest, live, gap }) {
  const currency = live.strategy.currency;
  const gapR = gap.expectancy_r;
  const hasR = gapR !== null && gapR !== undefined;
  const positive = hasR && gapR >= 0;
  const VerdictIcon = positive ? TrendingUp : TrendingDown;

  const relativeChange = useMemo(() => {
    const base = backtest.metrics.expectancy_r;
    if (!hasR || base === null || base === undefined || base === 0) {
      return null;
    }
    return (gapR / Math.abs(base)) * 100;
  }, [backtest.metrics.expectancy_r, gapR, hasR]);

  const minTrades = Math.min(backtest.metrics.total_trades, live.metrics.total_trades);

  const deltaRows = [
    {
      label: "Expectancy (R)",
      backtest: formatNumber(backtest.metrics.expectancy_r, 2, { sign: true }),
      live: formatNumber(live.metrics.expectancy_r, 2, { sign: true }),
      delta: gap.expectancy_r,
      digits: 2,
    },
    {
      label: `Expectancy (${currency})`,
      backtest: formatNumber(backtest.metrics.expectancy, 2, { sign: true }),
      live: formatNumber(live.metrics.expectancy, 2, { sign: true }),
      delta: gap.expectancy,
      digits: 2,
    },
    {
      label: "Win rate",
      backtest: formatNumber(backtest.metrics.win_rate, 1, { suffix: "%" }),
      live: formatNumber(live.metrics.win_rate, 1, { suffix: "%" }),
      delta: gap.win_rate,
      digits: 1,
      suffix: "pp",
    },
    {
      label: "Profit factor",
      backtest: formatNumber(backtest.metrics.profit_factor, 2),
      live: formatNumber(live.metrics.profit_factor, 2),
      delta: gap.profit_factor,
      digits: 2,
    },
    {
      label: `Total P/L (${currency})`,
      backtest: formatNumber(backtest.metrics.total_pnl, 2, { sign: true }),
      live: formatNumber(live.metrics.total_pnl, 2, { sign: true }),
      delta: gap.total_pnl,
      digits: 2,
    },
    {
      label: "Return on capital",
      backtest: formatNumber(backtest.metrics.return_on_capital, 2, { suffix: "%", sign: true }),
      live: formatNumber(live.metrics.return_on_capital, 2, { suffix: "%", sign: true }),
      delta: gap.return_on_capital,
      digits: 2,
      suffix: "pp",
    },
    {
      label: "Trades",
      backtest: `${backtest.metrics.total_trades}`,
      live: `${live.metrics.total_trades}`,
      delta: gap.trades,
      digits: 0,
    },
  ];

  return (
    <Card className="shadow-sm" data-testid="comparison-gap">
      <CardHeader className="pb-4">
        <CardTitle className="text-base">Execution gap (live − backtest)</CardTitle>
        <CardDescription>
          How your live execution performs against the same strategy on paper.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div
          className={`flex items-center gap-3 rounded-lg border px-4 py-3 ${
            hasR
              ? positive
                ? "border-success/40 bg-success/10 text-success"
                : "border-destructive/40 bg-destructive/10 text-destructive"
              : "border-border bg-secondary/40 text-muted-foreground"
          }`}
        >
          {hasR ? <VerdictIcon className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
          <div>
            <p className="text-sm font-semibold">
              {hasR
                ? positive
                  ? `Live expectancy is +${gapR.toFixed(2)}R above the backtest`
                  : `Live expectancy is ${gapR.toFixed(2)}R below the backtest`
                : "Not enough data to compute the R gap"}
            </p>
            {relativeChange !== null && (
              <p className="text-xs opacity-80">
                {relativeChange >= 0 ? "+" : ""}
                {relativeChange.toFixed(1)}% versus backtest expectancy
              </p>
            )}
          </div>
        </div>

        {minTrades < SAMPLE_MIN && (
          <div className="flex items-center gap-2 rounded-lg border border-orange-500/40 bg-orange-500/10 px-3 py-2 text-xs text-orange-400">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>
              Sample too small to be conclusive: the smallest side has {minTrades} trades (recommended
              minimum {SAMPLE_MIN}).
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <CompareBar
            label="Expectancy (R)"
            backtestValue={backtest.metrics.expectancy_r}
            liveValue={live.metrics.expectancy_r}
            format={(value) => formatNumber(value, 2, { sign: true })}
          />
          <CompareBar
            label="Win rate"
            backtestValue={backtest.metrics.win_rate}
            liveValue={live.metrics.win_rate}
            format={(value) => formatNumber(value, 1, { suffix: "%" })}
          />
          <CompareBar
            label="Profit factor"
            backtestValue={backtest.metrics.profit_factor}
            liveValue={live.metrics.profit_factor}
            format={(value) => formatNumber(value, 2)}
          />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2 pr-3 font-medium">Metric</th>
                <th className="py-2 pr-3 font-medium">Backtest</th>
                <th className="py-2 pr-3 font-medium">Live</th>
                <th className="py-2 font-medium">Δ</th>
              </tr>
            </thead>
            <tbody>
              {deltaRows.map((row) => (
                <tr key={row.label} className="border-b border-border/60 last:border-0">
                  <td className="py-2 pr-3 text-muted-foreground">{row.label}</td>
                  <td className="py-2 pr-3 font-medium">{row.backtest}</td>
                  <td className="py-2 pr-3 font-medium">{row.live}</td>
                  <td
                    className={`py-2 font-semibold ${
                      row.delta === null || row.delta === undefined
                        ? "text-muted-foreground"
                        : row.delta > 0
                        ? "text-success"
                        : row.delta < 0
                        ? "text-destructive"
                        : "text-muted-foreground"
                    }`}
                  >
                    {row.delta === null || row.delta === undefined
                      ? "—"
                      : `${row.delta > 0 ? "+" : ""}${Number(row.delta).toFixed(row.digits)}${
                          row.suffix ? ` ${row.suffix}` : ""
                        }`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function LinkDialog({ open, onOpenChange, otherMode, strategies, selectedId, onSelectId, onConfirm, saving }) {
  const hasStrategies = strategies.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="comparison-link-dialog">
        <DialogHeader>
          <DialogTitle>Link a {getModeLabel(otherMode)} strategy</DialogTitle>
          <DialogDescription>
            Pair this strategy with its counterpart in {getModeLabel(otherMode).toLowerCase()} mode to
            measure the execution gap.
          </DialogDescription>
        </DialogHeader>
        {hasStrategies ? (
          <div className="space-y-2">
            <Label htmlFor="linked-strategy">Strategy</Label>
            <Select value={selectedId} onValueChange={onSelectId}>
              <SelectTrigger id="linked-strategy" data-testid="comparison-link-select">
                <SelectValue placeholder={`Select a ${getModeLabel(otherMode).toLowerCase()} strategy`} />
              </SelectTrigger>
              <SelectContent>
                {strategies.map((strategy) => (
                  <SelectItem key={strategy.id} value={strategy.id}>
                    {strategy.name} · {strategy.currency}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            No strategies in {getModeLabel(otherMode).toLowerCase()} mode yet. Create one in{" "}
            {getModeLabel(otherMode).toLowerCase()} mode first, then come back to link it.
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={!hasStrategies || !selectedId || saving}
            data-testid="comparison-link-confirm"
          >
            <Link2 className="mr-2 h-4 w-4" />
            Link strategies
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LoadingSkeleton() {
  return (
    <div className="p-4 md:p-6">
      <Skeleton className="mb-2 h-9 w-56" />
      <Skeleton className="mb-6 h-4 w-80" />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Skeleton className="h-72 rounded-xl" />
        <Skeleton className="h-72 rounded-xl" />
      </div>
      <Skeleton className="mt-6 h-80 rounded-xl" />
    </div>
  );
}

export default function ComparisonView({ currentStrategy, mode }) {
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [otherStrategies, setOtherStrategies] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [saving, setSaving] = useState(false);

  const otherMode =
    mode === TRADING_MODES.BACKTESTING ? TRADING_MODES.LIVE : TRADING_MODES.BACKTESTING;

  const loadComparison = useCallback(async () => {
    if (!currentStrategy) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${API}/comparison/${currentStrategy.id}?mode=${mode}`, {
        headers: authHeaders(),
      });
      setComparison(response.data);
    } catch (error) {
      toast.error("Failed to load the execution gap comparison");
      setComparison(null);
    } finally {
      setLoading(false);
    }
  }, [currentStrategy, mode]);

  const loadOtherStrategies = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/strategies?mode=${otherMode}`, {
        headers: authHeaders(),
      });
      setOtherStrategies(response.data);
    } catch (error) {
      setOtherStrategies([]);
    }
  }, [otherMode]);

  useEffect(() => {
    loadComparison();
  }, [loadComparison]);

  useEffect(() => {
    loadOtherStrategies();
  }, [loadOtherStrategies]);

  const handleLink = async () => {
    if (!selectedId) {
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/strategies/${currentStrategy.id}/link?mode=${mode}`,
        { linked_strategy_id: selectedId },
        { headers: authHeaders() }
      );
      toast.success("Strategies linked");
      setDialogOpen(false);
      setSelectedId("");
      await loadComparison();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409) {
        toast.error("That strategy is already linked to another one");
      } else {
        toast.error(detail || "Failed to link the strategies");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleUnlink = async () => {
    if (!currentStrategy) {
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/strategies/${currentStrategy.id}/link?mode=${mode}`,
        { linked_strategy_id: null },
        { headers: authHeaders() }
      );
      toast.success("Strategies unlinked");
      await loadComparison();
    } catch (error) {
      toast.error("Failed to unlink the strategies");
    } finally {
      setSaving(false);
    }
  };

  const sides = useMemo(() => {
    if (!comparison?.linked) {
      return null;
    }
    return comparison.base.strategy.mode === TRADING_MODES.BACKTESTING
      ? { backtest: comparison.base, live: comparison.linked }
      : { backtest: comparison.linked, live: comparison.base };
  }, [comparison]);

  if (!currentStrategy) {
    return (
      <div className="p-4 md:p-6" data-testid="comparison-no-strategy">
        <EmptyState
          icon={Scale}
          title="No strategy selected"
          description="Create or select a strategy to compare its backtest and live performance."
        />
      </div>
    );
  }

  if (loading) {
    return <LoadingSkeleton />;
  }

  const linkBroken = comparison?.link_broken;
  const isLinked = Boolean(comparison?.linked);

  return (
    <div className="p-4 md:p-6" data-testid="comparison-page">
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold md:text-3xl">
            <Scale className="h-7 w-7 text-primary" />
            Execution Gap
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Compare {currentStrategy.name} across backtesting and live trading.
          </p>
        </div>
        <div className="flex gap-2">
          {isLinked ? (
            <>
              <Button
                variant="outline"
                onClick={() => setDialogOpen(true)}
                data-testid="comparison-change-link"
              >
                <Link2 className="mr-2 h-4 w-4" />
                Change link
              </Button>
              <Button
                variant="outline"
                className="border-destructive/40 text-destructive hover:bg-destructive/10"
                onClick={handleUnlink}
                disabled={saving}
                data-testid="comparison-unlink"
              >
                <Unlink className="mr-2 h-4 w-4" />
                Unlink
              </Button>
            </>
          ) : (
            <Button onClick={() => setDialogOpen(true)} data-testid="comparison-link-button">
              <Link2 className="mr-2 h-4 w-4" />
              Link a strategy
            </Button>
          )}
        </div>
      </div>

      {!isLinked && (
        <div data-testid="comparison-empty">
          <EmptyState
            icon={Scale}
            title={linkBroken ? "Linked strategy no longer exists" : "No linked strategy yet"}
            description={
              linkBroken
                ? "The strategy linked to this one was deleted. Unlink to clean up, then link a new counterpart."
                : `Link this strategy to its ${getModeLabel(otherMode).toLowerCase()} counterpart to measure how live execution performs against the backtest.`
            }
            actionLabel={linkBroken ? undefined : "Link a strategy"}
            onAction={linkBroken ? undefined : () => setDialogOpen(true)}
          />
          {linkBroken && (
            <div className="mt-4 flex justify-center">
              <Button
                variant="outline"
                className="border-destructive/40 text-destructive hover:bg-destructive/10"
                onClick={handleUnlink}
                disabled={saving}
                data-testid="comparison-unlink-broken"
              >
                <Unlink className="mr-2 h-4 w-4" />
                Unlink
              </Button>
            </div>
          )}
        </div>
      )}

      {isLinked && sides && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <SideCard side={sides.backtest} />
            <SideCard side={sides.live} />
          </div>
          <GapPanel backtest={sides.backtest} live={sides.live} gap={comparison.gap} />
        </motion.div>
      )}

      <LinkDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        otherMode={otherMode}
        strategies={otherStrategies}
        selectedId={selectedId}
        onSelectId={setSelectedId}
        onConfirm={handleLink}
        saving={saving}
      />
    </div>
  );
}
